import dayjs from 'dayjs'
import { RefreshCw } from 'lucide-react'
import { Trans } from 'react-i18next'
import { useMaintenanceMode } from '~/common/hooks/useMaintenanceMode'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'

/**
 * Formats the maintenance window in the viewer's locale & timezone,
 * e.g. { start: "Oct 16, 2026, 5:00 PM", end: "6:30 PM PDT" }.
 * Returns null if either bound is missing or unparseable, so the page falls back to the downtime copy.
 */
const formatMaintenanceWindow = (
  startsAt: string | null,
  endsAt: string | null,
): { start: string; end: string } | null => {
  if (!startsAt || !endsAt) {
    return null
  }
  const start = dayjs(startsAt)
  const end = dayjs(endsAt)
  if (!start.isValid() || !end.isValid()) {
    return null
  }
  // This guard wraps the whole app, so a formatting error must fall back to the generic copy,
  // not crash every page.
  try {
    return {
      start: new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
        start.valueOf(),
      ),
      // `timeStyle` can't be combined with `timeZoneName` (throws a RangeError), so spell out the fields
      end: new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(end.valueOf()),
    }
  } catch {
    return null
  }
}

/**
 * Guard for maintenance mode. Fetches the server's maintenance mode status and shows
 * the maintenance UI when enabled. Polls the backend every 60 seconds to detect changes,
 * so as soon as the backend deploy completes with MAINTENANCE_MODE=true, users will see
 * the maintenance page without needing a frontend redeploy.
 */
export function MaintenanceModeGuard({ children }: { children: React.ReactNode }) {
  const { t } = useTypedTranslation()
  const { isLoading, maintenanceMode, maintenanceStartsAt, maintenanceEndsAt } =
    useMaintenanceMode()

  // While loading, render children optimistically to avoid flash of loading state
  // The maintenance UI will appear once we confirm maintenance mode is active
  if (isLoading) {
    return <>{children}</>
  }

  if (maintenanceMode) {
    const maintenanceWindow = formatMaintenanceWindow(maintenanceStartsAt, maintenanceEndsAt)
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-2 p-4">
        <div className="w-full max-w-lg rounded bg-white p-8 shadow-lg">
          <div className="text-center">
            <h1 className="mb-4 text-3xl text-blue-9">Scheduled Maintenance</h1>

            <div className="mb-6">
              <p className="mb-2 text-base text-gray-10">{t('maintenance_mode.description')}</p>
              <p className="mb-4 text-base text-gray-10">
                {maintenanceWindow ? (
                  <Trans
                    components={{ span: <span /> }}
                    i18nKey="maintenance_mode.window"
                    values={maintenanceWindow}
                  />
                ) : (
                  t('maintenance_mode.downtime')
                )}
              </p>
              <p className="mb-6 text-base text-gray-10">
                <Trans components={{ span: <span /> }} i18nKey="maintenance_mode.upgrade_impact" />
              </p>
            </div>

            <button
              className="mb-6 inline-flex items-center rounded bg-gray-10 px-4 py-2 text-base text-white transition-colors hover:bg-gray-11"
              onClick={() => window.location.reload()}
            >
              <RefreshCw className="me-2 size-5" />
              {t('maintenance_mode.refresh_page')}
            </button>

            <div className="text-sm text-gray-9">
              <p>{t('maintenance_mode.contact_support')}</p>
              <a
                className="font-normal text-blue-8 hover:text-blue-8"
                href="mailto:support@doss.com"
              >
                support@doss.com
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
